/**
 * API Request Validation Utilities
 * 
 * This module provides validation functions for API request payloads,
 * with a focus on validating GUID entity identifiers before requests
 * are sent to the backend.
 * 
 * All validation should happen at the API service layer (before axios calls)
 * to fail fast with clear error messages rather than receiving generic 400
 * errors from the backend.
 * 
 * @module apiValidation
 */

import { isValidGuid, formatGuidError } from './guidValidation';

/**
 * Provider / Graph IDs that happen to end in "Id" but are not CRM entity GUIDs
 * (Meta page id, lead form id, app id, ad id, etc.).
 */
const EXTERNAL_ID_FIELDS = new Set([
  'appid',
  'leadgenid',
  'facebookpageid',
  'pageid',
  'formid',
  'adid',
  'adsetid',
  'adaccountid',
  'metaleadid',
  'campaignid',
]);

function shouldSkipIdField(key, skipFields) {
  const lower = String(key || '').toLowerCase();
  if (EXTERNAL_ID_FIELDS.has(lower)) return true;
  return skipFields.some((field) => String(field || '').toLowerCase() === lower);
}

/** Check if running in development mode for enhanced logging */
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Development-only logger for GUID validation issues
 * @param {string} context - Where the validation occurred
 * @param {Object} details - Validation failure details
 */
function devLog(context, details) {
  if (isDevelopment) {
    console.group(`🔍 [GUID Validation] ${context}`);
    console.table(details);
    console.trace('Stack trace:');
    console.groupEnd();
  }
}

/**
 * Validates that all specified GUID fields in a payload are valid GUID format.
 * Throws an error if any field is present but not a valid GUID.
 * 
 * @param {Object} payload - The request payload to validate
 * @param {string[]} guidFieldNames - Array of field names that should contain GUIDs
 * @param {Object} [options] - Validation options
 * @param {boolean} [options.allowNull=true] - Whether null/undefined values are allowed
 * @param {boolean} [options.required=false] - Whether all fields must be present
 * @throws {Error} If any GUID field is invalid
 * 
 * @example
 * validateGuidFields(
 *   { clientId: "66f2cb93-...", subscriptionPlanId: "e4f5a6b7-..." },
 *   ['clientId', 'subscriptionPlanId']
 * );
 */
export function validateGuidFields(payload, guidFieldNames, options = {}) {
  const { allowNull = true, required = false } = options;
  
  if (!payload || typeof payload !== 'object') {
    throw new Error('Payload must be an object');
  }
  
  for (const fieldName of guidFieldNames) {
    const value = payload[fieldName];
    
    // Check if field is present
    if (value === undefined) {
      if (required) {
        throw new Error(`Required field "${fieldName}" is missing from request payload`);
      }
      continue; // Skip validation for optional undefined fields
    }
    
    // Check if field is null
    if (value === null) {
      if (!allowNull) {
        throw new Error(`Field "${fieldName}" cannot be null`);
      }
      continue; // Skip validation for allowed null fields
    }
    
    // Validate GUID format
    if (!isValidGuid(value)) {
      const error = formatGuidError(fieldName, value);
      devLog('validateGuidFields failure', {
        fieldName,
        value,
        type: typeof value,
        payload: JSON.stringify(payload),
      });
      throw new Error(error);
    }
  }
}

/**
 * Validates a single GUID field and throws if invalid.
 * Convenience wrapper for validateGuidFields with a single field.
 * 
 * @param {string} fieldName - The field name (for error message)
 * @param {any} value - The value to validate
 * @param {Object} [options] - Validation options
 * @param {boolean} [options.allowNull=false] - Whether null is allowed
 * @throws {Error} If GUID is invalid
 * 
 * @example
 * validateRequiredGuid('clientId', clientId);
 */
export function validateRequiredGuid(fieldName, value, options = {}) {
  const { allowNull = false } = options;
  
  if (value === null || value === undefined) {
    if (!allowNull) {
      throw new Error(`Required ${fieldName} is missing or null`);
    }
    return;
  }
  
  if (!isValidGuid(value)) {
    throw new Error(formatGuidError(fieldName, value));
  }
}

/**
 * Validates optional GUID fields (allows null/undefined).
 * Only validates if value is present and non-null.
 * 
 * @param {string} fieldName - The field name (for error message)
 * @param {any} value - The value to validate
 * @throws {Error} If GUID is present but invalid
 * 
 * @example
 * validateOptionalGuid('assignedUserId', assignedUserId);
 */
export function validateOptionalGuid(fieldName, value) {
  if (value === null || value === undefined || value === '') {
    return; // Optional field - skip validation
  }
  
  if (!isValidGuid(value)) {
    throw new Error(formatGuidError(fieldName, value));
  }
}

/**
 * Validates a request payload before sending to the API.
 * Checks all entity ID fields for GUID format.
 * 
 * This is a comprehensive validation that inspects common ID field patterns
 * and validates any field that looks like an entity identifier.
 * 
 * @param {Object} payload - The request payload
 * @param {Object} [options] - Validation options
 * @param {string[]} [options.requiredFields] - Fields that must be present and valid GUIDs
 * @param {string[]} [options.optionalFields] - Fields that are optional but must be valid GUIDs if present
 * @param {string[]} [options.skipFields] - Fields to skip validation even if they match ID patterns
 * @throws {Error} If validation fails
 * 
 * @example
 * validateRequestPayload(
 *   { clientId: "66f2cb93-...", name: "Test", userId: "8cff5b4b-..." },
 *   { requiredFields: ['clientId'], optionalFields: ['userId'] }
 * );
 */
export function validateRequestPayload(payload, options = {}) {
  const {
    requiredFields = [],
    optionalFields = [],
    skipFields = []
  } = options;
  
  if (!payload || typeof payload !== 'object') {
    return; // No payload to validate
  }
  
  // Validate required fields
  for (const fieldName of requiredFields) {
    validateRequiredGuid(fieldName, payload[fieldName]);
  }
  
  // Validate optional fields
  for (const fieldName of optionalFields) {
    validateOptionalGuid(fieldName, payload[fieldName]);
  }
  
  // Auto-detect and validate any field that looks like an entity ID
  // (only if not already validated above and not in skipFields)
  const idFieldPattern = /^(id|.*Id|.*_id)$/i;
  const validatedFields = new Set(
    [...requiredFields, ...optionalFields].map((field) => String(field).toLowerCase()),
  );

  for (const key of Object.keys(payload)) {
    const lowerKey = String(key).toLowerCase();
    if (validatedFields.has(lowerKey) || shouldSkipIdField(key, skipFields)) {
      continue;
    }

    if (idFieldPattern.test(key)) {
      const value = payload[key];
      if (value !== null && value !== undefined && value !== '') {
        // Catch leftover integer entity IDs. Do not require GUID format on every *Id
        // string — Meta page/form/app ids are numeric strings, not CRM GUIDs.
        if (typeof value === 'number') {
          console.error(`[API Validation] Field "${key}" is a number (${value}). Expected GUID string.`);
          throw new Error(`Field "${key}" must be a GUID string, not a number`);
        }
      }
    }
  }
}

/**
 * Wraps an API service function with automatic GUID validation.
 * 
 * @param {Function} serviceFn - The API service function to wrap
 * @param {Object} validationConfig - Validation configuration
 * @param {string[]} validationConfig.requiredGuids - Required GUID fields
 * @param {string[]} [validationConfig.optionalGuids] - Optional GUID fields
 * @returns {Function} Wrapped function that validates before calling original
 * 
 * @example
 * const createClientValidated = withGuidValidation(
 *   createClient,
 *   { requiredGuids: ['clientId'], optionalGuids: ['industryTemplateId'] }
 * );
 */
export function withGuidValidation(serviceFn, validationConfig) {
  return async function validatedServiceCall(...args) {
    const [payload, ...restArgs] = args;
    
    // Validate the payload (assumed to be first argument)
    if (payload && typeof payload === 'object') {
      const { requiredGuids = [], optionalGuids = [] } = validationConfig;
      
      for (const field of requiredGuids) {
        validateRequiredGuid(field, payload[field]);
      }
      
      for (const field of optionalGuids) {
        validateOptionalGuid(field, payload[field]);
      }
    }
    
    // Call original function with validated payload
    return serviceFn(payload, ...restArgs);
  };
}
